// Main-model controls shared by Mac and web Intelligence popovers.
import React, { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { coworkerApi } from '@/lib/api';
import { useCoworkerStore } from '@/store';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import PageSearchInput from '@/components/Inputs/PageSearchInput';

const effortLabel = value => ({ '': 'Auto', xhigh: 'Extra high', max: 'Maximum' }[value]
  || value.charAt(0).toUpperCase() + value.slice(1));

export default function WorkOrchestratorControls({ taskId, cachedModels, onModelsLoaded }) {
  const { colors } = useTheme();
  const task = useCoworkerStore(s => taskId ? s.tasks[taskId] : null);
  const composer = useCoworkerStore(s => s.composerOrchestrator);
  const setComposer = useCoworkerStore(s => s.setComposerOrchestrator);
  const setTask = useCoworkerStore(s => s.setTaskOrchestrator);
  const [models, setModels] = useState(cachedModels || null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [choosing, setChoosing] = useState(false);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [retry, setRetry] = useState(0);
  const savingRef = useRef(false);
  useEffect(() => {
    if (cachedModels) { setModels(cachedModels); setError(''); return; }
    let current = true;
    setModels(null); setError('');
    coworkerApi.getOrchestratorModels().then(items => {
      if (current) { setModels(items); onModelsLoaded?.(items); }
    })
      .catch(err => { if (current) setError(err.message || 'Could not load models'); });
    return () => { current = false; };
  }, [retry, cachedModels, onModelsLoaded]);
  const selected = taskId ? task : composer;
  const slug = selected?.orchestratorModel || '';
  const effort = selected?.reasoningEffort || '';
  const actualSlug = slug || task?.model;
  const model = models?.find(item => item.slug === actualSlug);
  const levels = model?.reasoningLevels || [];
  const disabled = saving || (taskId && (!task || task.status === 'queued'));
  const save = async (orchestratorModel, reasoningEffort) => {
    if (disabled || savingRef.current) return;
    savingRef.current = true; setSaving(true); setError(''); setNotice('');
    try {
      const settings = { orchestratorModel, reasoningEffort };
      if (taskId) {
        const out = await setTask(taskId, settings);
        setNotice(out.applied === 'next_turn' ? 'Applies on your next turn.' : 'Saved for your next message.');
      } else setComposer(settings);
      setChoosing(false); setQuery('');
    } catch (err) { setError(err.message || 'Could not save main model'); }
    finally { savingRef.current = false; setSaving(false); }
  };
  const row = { minHeight: 38, padding: 8, borderRadius: 8, flexDirection: 'row', alignItems: 'center', gap: 8 };
  const modelRow = (item) => <Pressable key={item.slug} disabled={disabled}
    accessibilityRole="button" accessibilityLabel={`Main model: ${item.name}`}
    accessibilityState={{ selected: slug === item.slug, disabled: !!disabled }}
    {...(Platform.OS === 'web' ? { 'aria-pressed': slug === item.slug } : {})}
    onPress={() => save(item.slug, '')}
    style={[row, slug === item.slug && { backgroundColor: colors.solid_color_tertiary }]}>
    <Text size="s" style={{ flex: 1 }} numberOfLines={2}>{item.name}</Text>
    {item.credits > 0 && <Text size="xxs" color="tertiary">from {item.credits} cr</Text>}
    {slug === item.slug && <Icon name="check" size={12} color={colors.text_color_primary} />}
  </Pressable>;
  // Also exclude Arya from older cached catalogs; Gab Work Agent is arya-agent.
  const filtered = models?.filter(item => item.slug !== 'arya'
    && `${item.name} ${item.slug}`.toLowerCase().includes(query.trim().toLowerCase())) || [];
  return <View style={{ paddingBottom: 14, marginBottom: 14, borderBottomWidth: 1, borderBottomColor: colors.border_color_secondary }}>
    <Text size="s" weight="semibold">Main model</Text>
    <Text size="xs" color="secondary" style={{ marginTop: 3, marginBottom: 8 }}>Plans and coordinates this Work task.</Text>
    <Pressable accessibilityRole="button" accessibilityLabel="Choose main Work model"
      accessibilityState={{ expanded: choosing, disabled: !!disabled }} disabled={disabled}
      {...(Platform.OS === 'web' ? { 'aria-expanded': choosing } : {})}
      onPress={() => setChoosing(!choosing)} style={[row, { borderWidth: 1, borderColor: colors.border_color_secondary }]}>
      <Icon name="brain" size={14} color={colors.text_color_secondary} />
      <Text size="s" numberOfLines={2} style={{ flex: 1 }}>{slug ? (model?.name || slug) : 'Auto'}</Text>
      <Icon name={choosing ? 'chevron-up' : 'chevron-down'} size={10} color={colors.text_color_secondary} />
    </Pressable>
    {choosing && <View style={{ marginTop: 8 }}>
      <PageSearchInput value={query} onChangeText={setQuery} placeholder="Search models" noPadding catalogVariant />
      <ScrollView style={{ maxHeight: 176, marginTop: 4 }} keyboardShouldPersistTaps="handled">
        {!query.trim() && modelRow({ slug: '', name: 'Auto' })}
        {filtered.map(modelRow)}
        {models && !filtered.length && <Text size="xs" color="secondary" style={{ padding: 8 }}>No matching models</Text>}
      </ScrollView>
    </View>}
    {!models && !error && <Text size="xs" color="secondary" style={{ marginTop: 8 }}>Loading models…</Text>}
    {!choosing && model && <>
      {levels.length > 0 ? <>
        <Text size="xs" color="secondary" style={{ marginTop: 12, marginBottom: 6 }}>Main model thinking</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5 }}>
          {['', ...levels].map(value => <Pressable key={value} disabled={disabled}
            accessibilityRole="button" accessibilityLabel={`Main model thinking: ${effortLabel(value)}`}
            accessibilityState={{ selected: effort === value, disabled: !!disabled }}
            {...(Platform.OS === 'web' ? { 'aria-pressed': effort === value } : {})}
            onPress={() => save(slug || actualSlug, value)}
            style={{ paddingHorizontal: 10, paddingVertical: 7, borderRadius: 7, borderWidth: 1,
              borderColor: effort === value ? colors.text_color_tertiary : colors.border_color_secondary,
              backgroundColor: effort === value ? colors.solid_color_tertiary : 'transparent' }}>
            <Text size="xs">{effortLabel(value)}</Text>
          </Pressable>)}
        </View>
      </> : <Text size="xs" color="secondary" style={{ marginTop: 8 }}>This model manages its own thinking.</Text>}
    </>}
    {saving && <Text size="xs" color="secondary" style={{ marginTop: 8 }}>Saving…</Text>}
    {!!notice && <Text size="xs" color="secondary" accessibilityLiveRegion="polite" style={{ marginTop: 8 }}>{notice}</Text>}
    {!!error && <View style={{ marginTop: 8 }}>
      <Text size="xs" accessibilityRole="alert" style={{ color: colors.text_color_danger || '#dc2626' }}>{error}</Text>
      {!models && <Pressable onPress={() => setRetry(retry + 1)} accessibilityRole="button" style={row}><Text size="xs">Retry loading models</Text></Pressable>}
    </View>}
  </View>;
}
